import { popupFeatures } from '@/containers/sequences/sender-connections/connect-mailbox.util';

/**
 * Connect an app in a window from Clu's chat. The OAuth flows (CRMs, Slack,
 * the workspace tools) all end on /integrations with an outcome param, and
 * their return URLs are fixed by the backend, so the window is marked in
 * sessionStorage instead: a window opened with `window.open` starts with a
 * copy of its opener's sessionStorage, and keeps it across the provider's
 * pages. The opener's own copy is removed straight after. When /integrations
 * loads in a marked window with an outcome, it says how it went over the
 * channel (same origin, so it works when the provider's pages have cut the
 * window's `opener`) and closes itself.
 */
export const CONNECT_POPUP_CHANNEL = 'cleanlist-connect-popup';
const MARKER_KEY = 'cleanlist:connect-popup';
/** Each attempt opens its own window: an open window found by name keeps the
 *  mark it was opened with, so a second app would report as the first. */
let attempts = 0;
const windowName = () =>
  `cleanlist-connect-popup-${Date.now().toString(36)}-${++attempts}`;

export type ConnectPopupReturn = {
  type: 'connect-popup-return';
  kind: string;
  ok: boolean;
  /** The provider's or the callback's error code, when it failed with one. */
  error?: string;
};

export function isConnectPopupReturn(
  message: unknown
): message is ConnectPopupReturn {
  return (
    !!message &&
    typeof message === 'object' &&
    (message as { type?: unknown }).type === 'connect-popup-return' &&
    typeof (message as { kind?: unknown }).kind === 'string' &&
    typeof (message as { ok?: unknown }).ok === 'boolean' &&
    ((message as { error?: unknown }).error === undefined ||
      typeof (message as { error?: unknown }).error === 'string')
  );
}

/**
 * Opens an empty window marked for `kind`. Call it from the click itself: a
 * window opened after an await is blocked. Null when the browser blocked it.
 */
export function openConnectPopup(kind: string): Window | null {
  try {
    window.sessionStorage.setItem(MARKER_KEY, kind);
  } catch {
    // No storage: the window lands on Integrations like a tab would.
  }
  const win = window.open('', windowName(), popupFeatures());
  try {
    window.sessionStorage.removeItem(MARKER_KEY);
  } catch {
    // Nothing was stored.
  }
  return win;
}

/**
 * Opens the window in the click, then sends it to the app's sign-in once
 * `authorizeUrl` answers. A blocked window falls back to the sign-in in this
 * tab, which returns to Integrations as the connect always did. A failed
 * request closes the window and rejects.
 */
export async function connectInPopup(
  kind: string,
  authorizeUrl: () => Promise<string>
): Promise<'popup' | 'redirect'> {
  const win = openConnectPopup(kind);
  let url: string;
  try {
    url = await authorizeUrl();
  } catch (error) {
    win?.close();
    throw error;
  }
  if (!win) {
    window.location.href = url;
    return 'redirect';
  }
  // The provider's pages get no handle on this tab; the window reports back
  // over the channel instead.
  win.opener = null;
  win.location.href = url;
  return 'popup';
}

/** The app this window was opened to connect, or null for any other window. */
export function connectPopupKind(): string | null {
  try {
    return window.sessionStorage.getItem(MARKER_KEY);
  } catch {
    return null;
  }
}

/**
 * In a marked window: tells the card that opened it how the connect ended,
 * with the error code when it failed, drops the mark and closes the window.
 * False (and nothing done) in any other window.
 */
export function finishConnectPopup(ok: boolean, error?: string): boolean {
  const kind = connectPopupKind();
  if (!kind) return false;
  try {
    window.sessionStorage.removeItem(MARKER_KEY);
  } catch {
    // Read but not removable: closing the window ends it anyway.
  }
  try {
    const channel = new BroadcastChannel(CONNECT_POPUP_CHANNEL);
    const message: ConnectPopupReturn = {
      type: 'connect-popup-return',
      kind,
      ok,
      ...(!ok && error ? { error } : {}),
    };
    channel.postMessage(message);
    channel.close();
  } catch {
    // No channel: the card reads the connection again on focus.
  }
  window.close();
  return true;
}

/** Calls `listener` with each window's word that a connect ended. */
export function onConnectPopupReturn(
  listener: (message: ConnectPopupReturn) => void
): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const channel = new BroadcastChannel(CONNECT_POPUP_CHANNEL);
  channel.onmessage = (event: MessageEvent) => {
    if (isConnectPopupReturn(event.data)) listener(event.data);
  };
  return () => channel.close();
}
